/**
 * DraftBanner.jsx — "pick up where you left off?"
 *
 * The autosave holds references only, so this is the moment the photos are
 * fetched: restoring loads the shoots it names from the delivery library first,
 * then puts the slides back. It only ever appears over an empty composer.
 */

import { useState } from 'react';
import { useHdphSites } from '../../hooks/useRotoTube';
import {
  applyDraft, dismissDraftPrompt, loadShoot, toast, useCarousel,
} from '../../hooks/useCarouselStore';

function when(iso) {
  const t = iso ? new Date(iso).getTime() : 0;
  if (!t) return '';
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return mins + ' minutes ago';
  if (mins < 60 * 24) return Math.round(mins / 60) + ' hours ago';
  const days = Math.round(mins / (60 * 24));
  return days === 1 ? 'yesterday' : days < 30 ? days + ' days ago' : new Date(t).toLocaleDateString();
}

export default function DraftBanner() {
  const prompt = useCarousel((s) => s.draftPrompt);
  const [busy, setBusy] = useState(false);
  const { data: sites, refetch } = useHdphSites(!!prompt);

  if (!prompt) return null;
  const shoots = (prompt.source && prompt.source.shoots) || [];
  const hasLocal = !!(prompt.source && prompt.source.hasLocal);

  const restore = async () => {
    setBusy(true);
    try {
      if (shoots.length) {
        let list = sites;
        if (!list) list = (await refetch()).data;
        for (const sh of shoots) {
          const site = (list || []).find((x) => x.sid === sh.sid);
          if (site) await loadShoot(site);
        }
      }
      if (hasLocal) toast('Some photos came from your computer — add them to fill those cells.', 5200);
      applyDraft(prompt.data);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="restore cc-draftbar">
      <span>
        Pick up where you left off? <b className="mono">{prompt.slideCount}</b>
        {prompt.slideCount === 1 ? ' slide' : ' slides'}
        {shoots.length ? ' · ' + shoots.map((x) => x.label).filter(Boolean).join(', ') : ''}
        {prompt.updatedAt ? ' · ' + when(prompt.updatedAt) : ''}
      </span>
      <span className="spacer" style={{ marginLeft: 'auto' }} />
      <button type="button" className="btn tiny" disabled={busy} onClick={restore}>
        {busy ? 'Loading photos…' : 'Restore'}
      </button>
      <button type="button" className="btn tiny ghost" disabled={busy} onClick={dismissDraftPrompt}>Not now</button>
    </div>
  );
}
